import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import CosmicLoveBackground from './CosmicLoveBackground';
import '../styles/Login.css';

const PIN_LENGTH = 6;

const Login = () => {
  const [pins, setPins] = useState(Array(PIN_LENGTH).fill(''));
  const [justEntered, setJustEntered] = useState({});
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [isShaking, setIsShaking] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [heartPulsing, setHeartPulsing] = useState(false);

  const cardRef = useRef(null);
  const inputRefs = useRef([]);
  const submittingRef = useRef(false);
  const { verifyPassword, completeLogin } = useAuth();

  // Fast direct 3D Tilt on pointer move (zero lag, hardware accelerated)
  const handlePointerMove = useCallback((e) => {
    const el = cardRef.current;
    if (!el || isUnlocked) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const normX = (x / rect.width - 0.5) * 2;
    const normY = (y / rect.height - 0.5) * 2;

    el.style.transform = `perspective(1000px) rotateX(${(-normY * 8).toFixed(2)}deg) rotateY(${(normX * 8).toFixed(2)}deg)`;
    el.style.setProperty('--glare-x', `${((x / rect.width) * 100).toFixed(1)}%`);
    el.style.setProperty('--glare-y', `${((y / rect.height) * 100).toFixed(1)}%`);
  }, [isUnlocked]);

  const handlePointerLeave = useCallback(() => {
    const el = cardRef.current;
    if (!el || isUnlocked) return;
    el.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg)';
    el.style.setProperty('--glare-x', '50%');
    el.style.setProperty('--glare-y', '50%');
  }, [isUnlocked]);

  // Mobile Gyroscope tilt
  useEffect(() => {
    const handleOrientation = (e) => {
      const el = cardRef.current;
      if (e.gamma !== null && e.beta !== null && el && !isUnlocked) {
        const tiltX = Math.max(-1, Math.min(1, e.gamma / 30));
        const tiltY = Math.max(-1, Math.min(1, (e.beta - 40) / 35));
        el.style.transform = `perspective(1000px) rotateX(${(-tiltY * 7).toFixed(2)}deg) rotateY(${(tiltX * 7).toFixed(2)}deg)`;
      }
    };

    if (window.DeviceOrientationEvent) {
      window.addEventListener('deviceorientation', handleOrientation, { passive: true });
    }
    return () => {
      if (window.DeviceOrientationEvent) {
        window.removeEventListener('deviceorientation', handleOrientation);
      }
    };
  }, [isUnlocked]);

  // Button Ripple Effect
  const triggerRipple = (e) => {
    const btn = e.currentTarget;
    const rect = btn.getBoundingClientRect();
    const ripple = document.createElement('span');
    ripple.className = 'key-ripple-silver';
    const size = Math.max(rect.width, rect.height) * 1.5;
    ripple.style.width = `${size}px`;
    ripple.style.height = `${size}px`;
    const clickX = e.clientX ? e.clientX - rect.left : rect.width / 2;
    const clickY = e.clientY ? e.clientY - rect.top : rect.height / 2;
    ripple.style.left = `${clickX - size / 2}px`;
    ripple.style.top = `${clickY - size / 2}px`;
    btn.appendChild(ripple);
    setTimeout(() => ripple.remove(), 550);
  };

  const doSubmit = async (password) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsLoading(true);
    setErrorMsg('');

    let isValid = false;
    try {
      isValid = await verifyPassword(password);
    } catch {
      isValid = false;
    }

    if (isValid) {
      setIsUnlocked(true);
      setIsLoading(false);

      setTimeout(() => {
        completeLogin();
      }, 880);
    } else {
      setErrorMsg('Yanlış şifre... 💔 Tekrar dener misin?');
      setIsShaking(true);
      setTimeout(() => {
        setIsShaking(false);
        setPins(Array(PIN_LENGTH).fill(''));
        setFocusedIndex(0);
        submittingRef.current = false;
        requestAnimationFrame(() => inputRefs.current[0]?.focus());
      }, 650);
      setIsLoading(false);
    }
  };

  const pressDigit = (d, e) => {
    if (isLoading || submittingRef.current || isUnlocked) return;
    if (e) triggerRipple(e);

    const idx = focusedIndex;
    if (idx >= PIN_LENGTH) return;

    const newPins = [...pins];
    newPins[idx] = d;
    setPins(newPins);

    setJustEntered((prev) => ({ ...prev, [idx]: true }));
    setTimeout(() => {
      setJustEntered((prev) => {
        const next = { ...prev };
        delete next[idx];
        return next;
      });
    }, 380);

    const next = Math.min(idx + 1, PIN_LENGTH - 1);
    setFocusedIndex(next);
    inputRefs.current[next]?.focus();

    if (newPins.every((p) => p !== '')) {
      doSubmit(newPins.join(''));
    }
  };

  const pressBackspace = (e) => {
    if (isLoading || isUnlocked) return;
    if (e) triggerRipple(e);

    const idx = focusedIndex;
    const newPins = [...pins];

    if (newPins[idx]) {
      newPins[idx] = '';
      setPins(newPins);
    } else if (idx > 0) {
      newPins[idx - 1] = '';
      setPins(newPins);
      setFocusedIndex(idx - 1);
      inputRefs.current[idx - 1]?.focus();
    }
  };

  const handleHeartClick = (e) => {
    if (e) triggerRipple(e);
    setHeartPulsing(true);
    setTimeout(() => setHeartPulsing(false), 700);
  };

  const handleKeyDown = (index, e) => {
    if (/^[0-9]$/.test(e.key)) {
      e.preventDefault();
      pressDigit(e.key);
    } else if (e.key === 'Backspace') {
      e.preventDefault();
      pressBackspace();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      setFocusedIndex(index - 1);
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < PIN_LENGTH - 1) {
      setFocusedIndex(index + 1);
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, PIN_LENGTH);
    if (!text) return;
    const newPins = Array(PIN_LENGTH).fill('');
    for (let i = 0; i < text.length; i++) newPins[i] = text[i];
    setPins(newPins);
    const next = Math.min(text.length, PIN_LENGTH - 1);
    setFocusedIndex(next);
    requestAnimationFrame(() => inputRefs.current[next]?.focus());
    if (text.length === PIN_LENGTH) doSubmit(text);
  };

  const handleKeypadMouseDown = (e) => e.preventDefault();

  return (
    <div
      className="login-container"
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
    >
      <CosmicLoveBackground />

      {/* Şifre Doğru Girildiğinde Gümüş-Bordo Işık Zerrecikleri */}
      {isUnlocked && (
        <div className="unlock-burst" aria-hidden="true">
          {Array.from({ length: 32 }).map((_, i) => (
            <span
              key={i}
              className="unlock-particle-silver"
              style={{
                '--angle': `${(i / 32) * 360}deg`,
                '--dist': `${Math.random() * 240 + 70}px`,
                '--dur': `${Math.random() * 0.3 + 0.6}s`,
                '--color': i % 2 === 0 ? '#f8fafc' : '#e11d48',
              }}
            />
          ))}
          <div className="unlock-beam-silver" />
        </div>
      )}

      {/* 3D Gothic Silver & Burgundy Glass PIN Kartı */}
      <div
        ref={cardRef}
        className={`login-box gothic-glass-card ${isShaking ? 'shake' : ''} ${isUnlocked ? 'cinematic-exit-silver' : ''}`}
      >
        <div className="glass-silver-glow" aria-hidden="true" />
        <div className="glass-chrome-specular" aria-hidden="true" />

        <div className="login-card-content">
          <h1 className="login-title">Yalnızca Edaskim için</h1>
          <p className="login-subtitle">Şifreni gir yavrum</p>

          {/* 6 Adet Sırayla Parlayan PIN Kutucuğu */}
          <div className="pin-row" onPaste={handlePaste} role="group" aria-label="PIN girişi">
            {pins.map((digit, i) => {
              const isFilled = Boolean(digit);
              const isCurrent = focusedIndex === i && !isFilled;
              return (
                <div
                  key={i}
                  className={`pin-wrapper ${isFilled ? 'filled' : ''} ${isCurrent ? 'active' : ''}`}
                >
                  <input
                    ref={(el) => (inputRefs.current[i] = el)}
                    type="text"
                    readOnly
                    value={digit ? (justEntered[i] ? digit : '●') : ''}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    onFocus={() => setFocusedIndex(i)}
                    className={`pin-box ${isFilled ? 'pin-filled' : ''} ${isShaking ? 'pin-error' : ''}`}
                    disabled={isLoading || isUnlocked}
                    autoFocus={i === 0}
                    autoComplete="off"
                    aria-label={`Rakam ${i + 1}`}
                  />
                  <div className="pin-silver-aura" aria-hidden="true" />
                </div>
              );
            })}
          </div>

          {errorMsg && <p className="login-error login-fade-in">{errorMsg}</p>}
          {isLoading && (
            <p className="login-loading">
              <span className="loading-heart">💓</span> Kontrol ediliyor...
            </p>
          )}

          {/* 3D Dokunsal Numlock Klavyesi */}
          <div className="pin-keypad" aria-label="Rakam klavyesi">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
              <button
                key={d}
                type="button"
                className="keypad-btn gothic-3d-key"
                onMouseDown={handleKeypadMouseDown}
                onClick={(e) => pressDigit(d, e)}
                disabled={isLoading || isUnlocked}
              >
                <span className="key-txt">{d}</span>
                <span className="key-chrome-top" aria-hidden="true" />
              </button>
            ))}

            {/* Alt satır: Kalp | 0 | Sil */}
            <button
              type="button"
              className={`keypad-btn gothic-3d-key key-heart ${heartPulsing ? 'pulse' : ''}`}
              onMouseDown={handleKeypadMouseDown}
              onClick={handleHeartClick}
              disabled={isLoading || isUnlocked}
              aria-label="Kalp"
            >
              <span className="heart-emoji">💕</span>
              <span className="key-chrome-top" aria-hidden="true" />
            </button>

            <button
              type="button"
              className="keypad-btn gothic-3d-key"
              onMouseDown={handleKeypadMouseDown}
              onClick={(e) => pressDigit('0', e)}
              disabled={isLoading || isUnlocked}
            >
              <span className="key-txt">0</span>
              <span className="key-chrome-top" aria-hidden="true" />
            </button>

            <button
              type="button"
              className="keypad-btn gothic-3d-key keypad-back"
              onMouseDown={handleKeypadMouseDown}
              onClick={pressBackspace}
              disabled={isLoading || isUnlocked}
              aria-label="Sil"
            >
              <span className="key-txt">⌫</span>
              <span className="key-chrome-top" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
